/**
 * vendors API (M-X8) — wraps gateway /api/vendors/status.
 *
 * The UI vendor picker uses this to disable rows for vendors the
 * signed-in person hasn't yet logged into (auth dir empty on host).
 *
 * WHOSE readiness (SPEC-03 / FD-A, 2026-08-01): the signed-in person's own, not
 * the founder's. The gateway resolves the tree through the same seam a spawn
 * uses (`lib/vendor-auth.ts`, rung 2 = the authenticated caller), so a green row
 * here and a container that actually starts are the same fact. Before that fix
 * this endpoint read the ONE shared tree, which with
 * PER_ACCOUNT_CLI_CREDENTIALS_ENABLED on would have shown every user the
 * FOUNDER's logins — green, then a refusal on the first turn.
 *
 * `ready` means "a login was completed in that tree", not "the token still
 * works": an expired or blanked refresh token leaves the marker file in place.
 * That limit predates per-account credentials and applies equally on both paths.
 *
 * WHICH IS WHY `live` EXISTS (2026-08-01). `ready` keeps its old meaning because
 * it GATES SELECTABILITY in the composer's picker — a warning must not be able
 * to take away the control it describes. `live` answers the harder question, and
 * `ready && !live` is exactly the 2026-07-26 state: ten hours during which the
 * platform could not run one agent while this endpoint would have said "ready".
 *
 * And `login*` below is the way IN. Before it, the only way to fill a credential
 * tree was a docker run under SSH on VPS2 — so a second person could not sign in
 * at all, and with per-account credentials on their first card refuses outright.
 * Nothing that could authenticate anybody travels back through these calls: the
 * gateway drives the vendor's own CLI in a throwaway container and returns two
 * pattern-checked strings. See apps/chaos-gateway/src/lib/vendor-login-session.ts.
 */
import {
  cardVendorInstanceChoiceSchema,
  cardVendorInstanceChoicesResponseSchema,
  decodeVendorLoginStatus,
  type CardVendorInstanceChoice,
  type VendorLoginStatus as SharedVendorLoginStatus,
} from '@chaos/types';
import { apiJson } from './client';
import type { Vendor } from './sessions';
import type { ResearchVendor } from './research';

/**
 * Readiness reason for a vendor:
 *   no_auth / cli_missing / auth_dir_missing — a CLI vendor this person hasn't
 *     logged into (subscription OAuth marker absent in their tree on host).
 *   no_key — an API-key vendor with no key in the vault. (2026-07-11)
 *   unattributed — per-account credentials are ON and this viewer resolves to no
 *     account, so there is no tree of theirs to look in. Never reported while
 *     the flag is off. The UI treats it exactly like the others (row disabled,
 *     "not logged in"): the remedy differs — an account, not a login — but the
 *     honest thing to show is that nothing here is usable. (2026-08-01)
 */
export type VendorReadyReason =
  | 'no_auth'
  | 'cli_missing'
  | 'auth_dir_missing'
  | 'no_key'
  | 'unattributed';

/**
 * Whether the login in that tree still WORKS, as opposed to whether one was
 * ever completed. Computed by the same module the 30-minute token watcher uses,
 * so the screen and the watcher cannot disagree.
 *
 *   unrenewable — the refresh token is gone. Nothing on the box can repair it.
 *   expired     — the access token lapsed; an automatic renewal may revive it.
 *   expiring    — alive, inside the 90-minute renewal window.
 *   unknown     — this vendor publishes no expiry we have measured (gemini), or
 *                 the credential is an API key. Never a guess.
 */
export type VendorLiveness =
  | 'absent'
  | 'unreadable'
  | 'unrenewable'
  | 'expired'
  | 'expiring'
  | 'live'
  | 'unknown';

export interface VendorStatus {
  vendor: Vendor;
  displayName: string;
  cliBinary: string;
  /** A login was completed in this person's tree. Gates the picker. */
  ready: boolean;
  reason?: VendorReadyReason;
  /** …and does it still work? */
  live: VendorLiveness;
  /** ISO expiry when the vendor publishes one. */
  expiresAt: string | null;
  /** The one state no automation can leave. Render this above everything. */
  needsHuman: boolean;
  /** Whether the in-app sign-in flow can drive this vendor. */
  canLogIn: boolean;
  authorization?: SharedVendorLoginStatus['authorization'];
  liveness?: SharedVendorLoginStatus['liveness'];
  runtime?: SharedVendorLoginStatus['runtime'];
}

function closeVendorStatus(row: VendorStatus): VendorStatus {
  const shared = decodeVendorLoginStatus({
    vendor: row.vendor,
    ready: row.ready,
    authorization: row.authorization,
    liveness: row.liveness,
    runtime: row.runtime,
  });
  if (shared) {
    return {
      ...row,
      authorization: shared.authorization,
      liveness: shared.liveness,
      runtime: shared.runtime,
    };
  }
  // Rolling-deploy, malformed, or future-enum responses are never promoted to
  // live from marker presence or from the legacy compatibility fields.
  return {
    ...row,
    authorization: { state: 'unknown', reason: 'marker_unreadable' },
    liveness: {
      state: 'unknown',
      reason: 'protocol_error',
      observedAt: null,
      retryAt: null,
    },
    runtime: {
      provider: row.vendor,
      cli: row.cliBinary,
      version: null,
      versionSource: 'unavailable',
      instanceOrdinal: null,
      directoryRelation: 'unavailable',
    },
  };
}

export type VendorLoginStatus =
  | 'starting'
  | 'awaiting_user'
  | 'awaiting_code'
  | 'complete'
  | 'failed'
  | 'expired'
  | 'cancelled';

export interface VendorLoginView {
  loginId: string;
  vendor: string;
  /** A3.9: which of the person's subscriptions this sign-in writes (absent from an older gateway = 1). */
  instanceOrdinal?: number;
  status: VendorLoginStatus;
  /** The vendor's own sign-in URL. Public by construction (PKCE challenge). */
  verificationUrl: string | null;
  /** Device code — codex only. */
  userCode: string | null;
  /** The CLI is blocked waiting for an authorization code to be pasted. */
  needsCode: boolean;
  expiresAt: string;
  credentialPresent: boolean;
  message: string;
}

export type SubscriptionWindowShape =
  | 'five_hour'
  | 'weekly'
  | 'weekly_opus'
  | 'weekly_sonnet';

export type SubscriptionSnapshotStatus =
  | 'connected'
  | 'stale'
  | 'error'
  | 'unsupported'
  | 'unknown';

export interface SubscriptionUsageWindow {
  shape: SubscriptionWindowShape;
  usedPercent: number | null;
  resetsAt: string | null;
}

/** Owner-only, sanitized SPEC-17 §6.3 view of one subscription instance. */
export interface SubscriptionUsageInstance {
  vendor: Vendor;
  instanceOrdinal: number;
  label: string | null;
  status: SubscriptionSnapshotStatus;
  windows: SubscriptionUsageWindow[];
  resetsAvailable?: number;
  resetSupported?: boolean;
  stale: boolean;
  fetchedAt: string | null;
}

/** Owner-only availability returned by GET /api/account/vendors. */
export interface AccountVendorCapabilities {
  subscriptionUsageTelemetry: boolean;
  /** A3.9: the deployment can run a second subscription of one vendor (absent = older gateway = no). */
  additionalSubscriptions?: boolean;
}

export type SubscriptionLinkStatus = 'disconnected' | 'connecting' | 'connected' | 'expired' | 'error';

/** One subscription instance of one vendor, as GET /api/account/vendors lists it. */
export interface AccountVendorInstanceView {
  instanceOrdinal: number;
  label: string | null;
  /** "Claude 2" or the person's own name (one rule, @chaos/types). Absent from an older gateway. */
  displayName?: string;
  status: SubscriptionLinkStatus;
  disabled: boolean;
  connectedAt: string | null;
  expiresAt: string | null;
  lastError: string | null;
  /**
   * OpenAI instances only (additive, 2026-10-28): the provider-checked health of
   * THIS subscription's own sign-in. Absent for Claude, for an instance that is
   * not signed in, and from an older gateway. Decoded fail-closed.
   */
  liveness?: SharedVendorLoginStatus['liveness'];
}

export interface AccountVendorView {
  lane: 'subscription' | 'api_key' | null;
  status: SubscriptionLinkStatus;
  instances: AccountVendorInstanceView[];
}

export interface AddSubscriptionInstanceResult {
  vendor: string;
  instanceOrdinal: number;
  reused: boolean;
  displayName: string;
  status: SubscriptionLinkStatus;
}

/** Answer of the per-subscription "Check now" (OpenAI). */
export interface CheckSubscriptionInstanceResult {
  vendor: string;
  instanceOrdinal: number;
  liveness: SharedVendorLoginStatus['liveness'];
}

/**
 * A liveness block that does not decode (a rolling deploy, a future enum value)
 * is never promoted to a verdict: it becomes "unknown / protocol_error".
 */
export function decodeInstanceLiveness(raw: unknown): SharedVendorLoginStatus['liveness'] {
  const shared = decodeVendorLoginStatus({
    vendor: 'openai',
    ready: true,
    authorization: { state: 'present' },
    liveness: raw,
    runtime: {
      provider: 'openai',
      cli: 'codex',
      version: null,
      versionSource: 'unavailable',
      instanceOrdinal: null,
      directoryRelation: 'unavailable',
    },
  });
  return (
    shared?.liveness ?? { state: 'unknown', reason: 'protocol_error', observedAt: null, retryAt: null }
  );
}

/**
 * Founder, 2026-10-28 (translated from Russian): "Let's remove the option to
 * connect Gemini from the interface for now. That is, don't delete the service
 * itself … but remove Gemini from the interface." Flip to true to bring the
 * Gemini sign-in row back; no backend or service code depends on it.
 */
export const GEMINI_SIGN_IN_SHOWN = false;

/** Is this vendor offered anywhere on the sign-in screens? */
export function isSignInVendorShown(vendor: string): boolean {
  return vendor !== 'google' || GEMINI_SIGN_IN_SHOWN;
}

/** `/vendors/login/<vendor>` for instance 1 (unchanged), `…/instances/<n>` for 2..N. */
function loginPath(vendor: string, instanceOrdinal?: number): string {
  const base = `/vendors/login/${encodeURIComponent(vendor)}`;
  return instanceOrdinal && instanceOrdinal > 1 ? `${base}/instances/${instanceOrdinal}` : base;
}

export type SubscriptionLimitReset =
  | { mode: 'automated' }
  | { mode: 'deep_link'; url: string };

/**
 * The 6-vendor superset the Research form consumes, served by the gateway at
 * /api/vendors/research-status. CLI vendors (anthropic/openai) carry auth-dir
 * readiness; the metered API vendors (google/perplexity/deepseek/xai) report
 * ready = key-present in the vault. Readiness is therefore DYNAMIC — the founder
 * adding a key via the Credentials UI flips a chip to selectable with no
 * redeploy (react-query refetches on the 60s staleTime / window focus).
 */
export interface ResearchVendorStatus {
  vendor: ResearchVendor;
  displayName: string;
  ready: boolean;
  reason?: VendorReadyReason;
  /** 'cli' = subscription CLI vendor; 'api' = metered API-key vendor. */
  kind: 'cli' | 'api';
}

export const vendorsApi = {
  status(): Promise<VendorStatus[]> {
    return apiJson<{ vendors: VendorStatus[] }>(`/vendors/status`).then((r) =>
      r.vendors.map(closeVendorStatus),
    );
  },
  /** Run the server-owned native OpenAI check for this authenticated account. */
  refreshOpenAiLiveness(): Promise<VendorStatus> {
    return apiJson<{ vendor: VendorStatus }>(`/vendors/openai/liveness`, {
      method: 'POST',
    }).then((r) => closeVendorStatus(r.vendor));
  },
  /**
   * Research readiness across all 6 vendors. Backed by the gateway
   * /api/vendors/research-status endpoint, which resolves the metered vendors'
   * keys from the vault at request time — so this reflects newly-added keys
   * without a code change or redeploy.
   */
  researchStatus(): Promise<ResearchVendorStatus[]> {
    return apiJson<{ vendors: ResearchVendorStatus[] }>(`/vendors/research-status`).then(
      (r) => r.vendors,
    );
  },

  /**
   * Begin (or re-attach to) a sign-in for the signed-in person's OWN account —
   * into instance `instanceOrdinal` of it when given (A3.9), else instance 1.
   */
  loginStart(vendor: string, instanceOrdinal?: number): Promise<VendorLoginView> {
    return apiJson<VendorLoginView>(`${loginPath(vendor, instanceOrdinal)}/start`, { method: 'POST' });
  },
  /** Poll. Safe to call on an interval; the container is the record. */
  loginStatus(vendor: string, instanceOrdinal?: number): Promise<VendorLoginView> {
    return apiJson<VendorLoginView>(loginPath(vendor, instanceOrdinal));
  },
  /**
   * The ONE value that travels inward, and only for Claude: the OAuth
   * authorization code the vendor's own callback page shows after approval.
   *
   * NOT a password and NOT a token — a single-use PKCE authorization code that
   * is worthless without the code_verifier, which never leaves the sign-in
   * container. The gateway writes it to that container's stdin and drops it: it
   * is never stored, never logged and never returned by any endpoint. This is
   * the same value the founder pastes into his terminal today, through a
   * strictly narrower pipe than the SSH session it replaces.
   */
  loginSubmitCode(vendor: string, code: string, instanceOrdinal?: number): Promise<VendorLoginView> {
    return apiJson<VendorLoginView>(`${loginPath(vendor, instanceOrdinal)}/code`, {
      method: 'POST',
      json: { code },
    });
  },
  loginCancel(vendor: string, instanceOrdinal?: number): Promise<VendorLoginView> {
    return apiJson<VendorLoginView>(loginPath(vendor, instanceOrdinal), { method: 'DELETE' });
  },

  /** Every vendor's subscription instances for the signed-in account owner (A3.9). */
  accountVendors(): Promise<{
    vendors: Record<string, AccountVendorView>;
    additionalSubscriptions: boolean;
  }> {
    return apiJson<{
      vendors: Record<string, AccountVendorView>;
      capabilities?: Partial<AccountVendorCapabilities>;
    }>(`/account/vendors`).then((r) => {
      const vendors = r.vendors ?? {};
      for (const view of Object.values(vendors)) {
        for (const instance of view?.instances ?? []) {
          if (instance.liveness !== undefined) instance.liveness = decodeInstanceLiveness(instance.liveness);
        }
      }
      return { vendors, additionalSubscriptions: r.capabilities?.additionalSubscriptions === true };
    });
  },
  /**
   * "Add subscription": a new instance row the next sign-in fills (or an
   * unfinished one). The optional name travels as `{label}`; no name = no body.
   */
  addSubscriptionInstance(vendor: string, label?: string): Promise<AddSubscriptionInstanceResult> {
    const name = label?.trim();
    return apiJson<AddSubscriptionInstanceResult>(
      `/account/vendors/${encodeURIComponent(vendor)}/instances`,
      name ? { method: 'POST', json: { label: name } } : { method: 'POST' },
    );
  },
  /**
   * "Check now" for ONE ChatGPT subscription: the provider's own verdict on that
   * sign-in. 503 `busy` / 429 `cooldown` carry `retryAt` in the error detail.
   */
  checkSubscriptionInstance(
    vendor: string,
    instanceOrdinal: number,
  ): Promise<CheckSubscriptionInstanceResult> {
    return apiJson<CheckSubscriptionInstanceResult>(
      `/account/vendors/${encodeURIComponent(vendor)}/instances/${instanceOrdinal}/check`,
      { method: 'POST' },
    ).then((r) => ({ ...r, liveness: decodeInstanceLiveness(r.liveness) }));
  },
  renameSubscriptionInstance(
    vendor: string,
    instanceOrdinal: number,
    label: string | null,
  ): Promise<{ displayName: string }> {
    return apiJson<{ displayName: string }>(
      `/account/vendors/${encodeURIComponent(vendor)}/instances/${instanceOrdinal}`,
      { method: 'PATCH', json: { label } },
    );
  },
  removeSubscriptionInstance(
    vendor: string,
    instanceOrdinal: number,
  ): Promise<{ removed: boolean; filesRemoved: boolean }> {
    return apiJson<{ removed: boolean; filesRemoved: boolean }>(
      `/account/vendors/${encodeURIComponent(vendor)}/instances/${instanceOrdinal}`,
      { method: 'DELETE' },
    );
  },

  /** The card's subscription picker rows for the signed-in person (A3.9 + A18). */
  cardSubscriptionInstances(cardId: string): Promise<CardVendorInstanceChoice[]> {
    return apiJson<unknown>(`/cards/${encodeURIComponent(cardId)}/vendor-instances`).then(
      (r) => cardVendorInstanceChoicesResponseSchema.parse(r).vendors,
    );
  },
  /** Pick (or clear with null) which of my subscriptions runs my agents on this card. */
  setCardSubscriptionInstance(
    cardId: string,
    vendor: string,
    instanceOrdinal: number | null,
  ): Promise<CardVendorInstanceChoice> {
    return apiJson<unknown>(
      `/cards/${encodeURIComponent(cardId)}/vendor-instances/${encodeURIComponent(vendor)}`,
      { method: 'PUT', json: { instanceOrdinal } },
    ).then((r) => cardVendorInstanceChoiceSchema.parse(r));
  },

  /** Whether this owner may read subscription telemetry on this deployment. */
  accountCapabilities(): Promise<AccountVendorCapabilities> {
    return apiJson<{ capabilities: AccountVendorCapabilities }>(`/account/vendors`).then(
      (r) => {
        if (typeof r?.capabilities?.subscriptionUsageTelemetry !== 'boolean') {
          throw new Error('subscription_usage_capability_unavailable');
        }
        return r.capabilities;
      },
    );
  },

  /** Every subscription instance owned by the authenticated account. */
  usageLimits(): Promise<SubscriptionUsageInstance[]> {
    // `?? []`: the readings share ONE cache entry with the header strip
    // (lib/subscription-limits.ts), and a query may never resolve undefined.
    return apiJson<{ instances?: SubscriptionUsageInstance[] }>(`/account/usage-limits`).then(
      (r) => r.instances ?? [],
    );
  },

  /** Official-CLI reset. The caller owns the idempotency key across a retry. */
  resetUsageLimit(
    vendor: string,
    instanceOrdinal: number,
    idempotencyKey: string,
  ): Promise<SubscriptionLimitReset> {
    return apiJson<SubscriptionLimitReset>(
      `/account/vendors/${encodeURIComponent(vendor)}/instances/${instanceOrdinal}/reset`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
      },
    );
  },
};

export const vendorsKeys = {
  all: ['vendors'] as const,
  status: () => [...vendorsKeys.all, 'status'] as const,
  researchStatus: () => [...vendorsKeys.all, 'research-status'] as const,
  login: (vendor: string, instanceOrdinal = 1) =>
    instanceOrdinal > 1
      ? ([...vendorsKeys.all, 'login', vendor, instanceOrdinal] as const)
      : ([...vendorsKeys.all, 'login', vendor] as const),
  accountVendors: () => [...vendorsKeys.all, 'account-vendors'] as const,
  cardSubscriptionInstances: (cardId: string) =>
    [...vendorsKeys.all, 'card-subscription-instances', cardId] as const,
  accountCapabilities: () => [...vendorsKeys.all, 'account-capabilities'] as const,
  usageLimits: () => [...vendorsKeys.all, 'usage-limits'] as const,
};
